import React, { useEffect, useState } from 'react';
import { Box, Card, CardContent, Typography, Button, TextField, Snackbar, Alert, Paper, Table, TableHead, TableRow, TableCell, TableBody, CircularProgress, Grid, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, IconButton } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import AddIcon from '@mui/icons-material/Add';

const Proposals = () => {
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ project_id: '', title: '', uploader: '', description: '', file: null });
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filters, setFilters] = useState({ q: '', uploader: '', from: '', to: '' });
  const [projects, setProjects] = useState([]);
  const [projectDialogOpen, setProjectDialogOpen] = useState(false);
  const [newProject, setNewProject] = useState({ name: '', description: '' });
  const [projectError, setProjectError] = useState('');
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);

  const fetchProposals = () => {
    setLoading(true);
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
    fetch('/api/proposals?' + params.toString())
      .then(res => res.json())
      .then(data => {
        setProposals(data);
        setLoading(false);
      });
  };

  const fetchProjects = () => {
    fetch('/api/projects')
      .then(res => res.json())
      .then(data => setProjects(data));
  };

  useEffect(() => { fetchProposals(); }, [filters]);
  useEffect(() => { fetchProjects(); }, []);

  const handleChange = e => {
    const { name, value, files } = e.target;
    setForm(f => ({ ...f, [name]: files ? files[0] : value }));
  };

  const handleFilterChange = e => {
    const { name, value } = e.target;
    setFilters(f => ({ ...f, [name]: value }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setUploading(true);
    setError('');
    setSuccess('');
    const formData = new FormData();
    Object.entries(form).forEach(([key, value]) => { if (value) formData.append(key, value); });
    try {
      const res = await fetch('/api/proposals', { method: 'POST', body: formData });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Upload failed');
      setSuccess('Proposal uploaded successfully!');
      setForm({ project_id: '', title: '', uploader: '', description: '', file: null });
      setUploadDialogOpen(false);
      fetchProposals();
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  // Project dialog handlers
  const handleProjectDialogOpen = () => { setProjectDialogOpen(true); setNewProject({ name: '', description: '' }); setProjectError(''); };
  const handleProjectDialogClose = () => setProjectDialogOpen(false);
  const handleNewProjectChange = e => setNewProject(p => ({ ...p, [e.target.name]: e.target.value }));
  const handleAddProject = async () => {
    if (!newProject.name) { setProjectError('Project name is required'); return; }
    setProjectError('');
    try {
      const res = await fetch('/api/projects', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newProject)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to add project');
      setProjects(prev => [...prev, data]);
      setForm(f => ({ ...f, project_id: data.id }));
      setProjectDialogOpen(false);
    } catch (err) {
      setProjectError(err.message);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">Proposals</Typography>
        <Button variant="contained" startIcon={<CloudUploadIcon />} onClick={() => setUploadDialogOpen(true)}>
          Upload Proposal
        </Button>
      </Box>
      <Grid container spacing={3}>
        <Grid item xs={12} md={12}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Filter Proposals</Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
              <TextField label="Keyword" name="q" value={filters.q} onChange={handleFilterChange} size="small" />
              <TextField label="Uploader" name="uploader" value={filters.uploader} onChange={handleFilterChange} size="small" />
              <TextField label="From" name="from" type="date" value={filters.from} onChange={handleFilterChange} size="small" InputLabelProps={{ shrink: true }} />
              <TextField label="To" name="to" type="date" value={filters.to} onChange={handleFilterChange} size="small" InputLabelProps={{ shrink: true }} />
            </Box>
            <Typography variant="h6" sx={{ mb: 2 }}>Proposals List</Typography>
            {loading ? <CircularProgress /> : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Title</TableCell>
                    <TableCell>Uploader</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Download</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {proposals.map(p => (
                    <TableRow key={p.id}>
                      <TableCell>{p.id}</TableCell>
                      <TableCell>{p.title}</TableCell>
                      <TableCell>{p.uploader}</TableCell>
                      <TableCell>{p.description}</TableCell>
                      <TableCell>{new Date(p.created_at).toLocaleString()}</TableCell>
                      <TableCell>
                        <Button href={`/api/proposals/${p.id}/download`} target="_blank" rel="noopener noreferrer" startIcon={<DownloadIcon />}>Download</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Paper>
        </Grid>
      </Grid>
      <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Upload Proposal</DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                label="Project"
                name="project_id"
                value={form.project_id}
                onChange={handleChange}
                size="small"
                select
                fullWidth
              >
                <MenuItem value="">None</MenuItem>
                {projects.map(p => (
                  <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                ))}
              </TextField>
              <IconButton onClick={handleProjectDialogOpen} color="primary" size="small" sx={{ ml: 1 }} title="Add Project">
                <AddIcon />
              </IconButton>
            </Box>
            <TextField label="Title" name="title" value={form.title} onChange={handleChange} size="small" required />
            <TextField label="Uploader" name="uploader" value={form.uploader} onChange={handleChange} size="small" required />
            <TextField label="Description" name="description" value={form.description} onChange={handleChange} size="small" multiline rows={2} />
            <Button variant="contained" component="label" startIcon={<CloudUploadIcon />} disabled={uploading}>
              {form.file ? form.file.name : 'Select File'}
              <input name="file" type="file" hidden onChange={handleChange} required />
            </Button>
            <Button type="submit" variant="contained" disabled={uploading} sx={{ mt: 1 }}>{uploading ? 'Uploading...' : 'Upload Proposal'}</Button>
          </Box>
        </DialogContent>
      </Dialog>
      <Dialog open={projectDialogOpen} onClose={handleProjectDialogClose}>
        <DialogTitle>Add Project</DialogTitle>
        <DialogContent sx={{ minWidth: 320 }}>
          <TextField label="Project Name" name="name" value={newProject.name} onChange={handleNewProjectChange} fullWidth sx={{ mb: 2 }} required />
          <TextField label="Description" name="description" value={newProject.description} onChange={handleNewProjectChange} fullWidth multiline rows={2} />
          {projectError && <Alert severity="error" sx={{ mt: 1 }}>{projectError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleProjectDialogClose}>Cancel</Button>
          <Button onClick={handleAddProject} variant="contained">Add</Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={!!error} autoHideDuration={4000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={() => setError('')} severity="error">{error}</Alert>
      </Snackbar>
      <Snackbar open={!!success} autoHideDuration={3000} onClose={() => setSuccess('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={() => setSuccess('')} severity="success">{success}</Alert>
      </Snackbar>
    </Box>
  );
};

export default Proposals; 